import { User } from "@/types";
import React, { createContext, useContext, useState, useEffect } from "react";
import { authService } from "@/api/auth";
import { appConfig } from "@/config/appConfig";

interface AuthContextType {
  user: User | null;
  login: (data: User) => void;
  updateUser: (updates: Partial<User>) => void;
  logout: () => Promise<void>;
  isLoading: boolean;
  refetchUser: () => Promise<void>;
  isSuperHR: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Fetch current user from backend on mount
  useEffect(() => {
    const fetchUser = async () => {
      // Skip fetching user on public routes or callback where we expect to be unauthenticated initially
      const pathname = window.location.pathname;
      if (pathname === "/callback" || pathname === "/login") {
        setIsLoading(false);
        return;
      }

      try {
        const userData = await authService.me();
        setUser(userData as User);
      } catch {
        // User is not authenticated or session expired
        setUser(null);
      } finally {
        setIsLoading(false);
      }
    };

    fetchUser();
  }, []);

  const login = (userData: User) => {
    setUser(userData);
  };

  const updateUser = (updates: Partial<User>) => {
    setUser((prev) => (prev ? { ...prev, ...updates } : prev));
  };

  const logout = async () => {
    try {
      await authService.logout();
      setUser(null);
      window.location.href = "/login";
    } catch (error) {
      console.error("Logout failed:", error);
      setUser(null);
      window.location.href = "/login";
    }
  };

  const refetchUser = async () => {
    try {
      const userData = await authService.me();
      setUser(userData as User);
    } catch (error) {
      setUser(null);
      throw error;
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        login,
        updateUser,
        logout,
        isLoading,
        refetchUser,
        isSuperHR:
          !!user?.roles.includes("hr") &&
          user?.email === appConfig.superHREmail,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
